// Mock data for the application

import type { AnalysisCardsByFilterType } from "@/components/AnalysisSection";
import type { InvoiceByFilter } from "@/components/InvoiceManagementTable";
import type { OrderSummaryRecord } from "@/components/OrdersPaymentSummaryTable";
import type { ProjectsByFilter } from "@/components/WorkInProgress";
import type { TabType } from "@/pages/Dashboard";

export const mockInventoryData = [
  {
    material: "Cement",
    currentStock: 230,
    unit: "Bags",
    minLevel: 300,
    status: "🔴 Low Stock",
    action: "Reorder",
    actionType: "secondary",
  },
  {
    material: "Steel Rod TMT 12mm",
    currentStock: 8.2,
    unit: "Tons",
    minLevel: 5,
    status: "🟢 OK",
    action: "View",
    actionType: "primary",
  },
  {
    material: "Aggregates 20mm",
    currentStock: 40,
    unit: "Tons",
    minLevel: 30,
    status: "🟢 OK",
    action: "View",
    actionType: "primary",
  },
  {
    material: "Bricks (Red Clay)",
    currentStock: 15000,
    unit: "Pieces",
    minLevel: 20000,
    status: "🔴 Low Stock",
    action: "Reorder",
    actionType: "secondary",
  },
];

export const mockMachineUsageData = [
  {
    equipment: "Excavator CAT 320D",
    type: "Heavy",
    project: "Highway Bridge Project",
    operator: "Mike Johnson",
    hoursUsed: 156,
    lastService: "05-Apr",
    nextDue: "20-Apr",
    priority: "High",
    priorityColor: "bg-red-500",
    status: "🟢 Active",
    action: "Details",
    actionType: "primary",
  },
  {
    equipment: "Concrete Mixer 350L",
    type: "Medium",
    project: "Downtown Office Complex",
    operator: "John Smith",
    hoursUsed: 42,
    lastService: "12-Apr",
    nextDue: "26-Apr",
    priority: "Scheduled",
    priorityColor: "bg-yellow-500",
    status: "🟢 Active",
    action: "Details",
    actionType: "primary",
  },
  {
    equipment: "Tower Crane TC5613",
    type: "Heavy",
    project: "City Mall Renovation",
    operator: "Sarah Williams",
    hoursUsed: 89,
    lastService: "18-Apr",
    nextDue: "02-May",
    priority: "Low",
    priorityColor: "bg-green-500",
    status: "🟡 Maintenance",
    action: "Details",
    actionType: "primary",
  },
  {
    equipment: "Bulldozer D8T",
    type: "Heavy",
    project: "Industrial Park",
    operator: "Robert Brown",
    hoursUsed: 120,
    lastService: "22-Apr",
    nextDue: "06-May",
    priority: "Scheduled",
    priorityColor: "bg-yellow-500",
    status: "🟢 Active",
    action: "Details",
    actionType: "primary",
  },
];

export const mockEquipmentData = [
  {
    id: "EQ-001",
    name: "Concrete Mixer 350L",
    category: "Heavy Equipment",
    status: "Active",
    location: "Site A",
    lastMaintenance: "2024-01-15",
  },
  {
    id: "EQ-002",
    name: "Excavator CAT 320D",
    category: "Heavy Equipment",
    status: "Active",
    location: "Site B",
    lastMaintenance: "2024-01-10",
  },
  {
    id: "EQ-003",
    name: "Tower Crane TC5613",
    category: "Heavy Equipment",
    status: "Maintenance",
    location: "Site C",
    lastMaintenance: "2024-01-20",
  },
];

export const mockMaintenanceData = [
  {
    equipment: "Concrete Mixer 350L",
    type: "Preventive",
    scheduledDate: "2024-02-01",
    status: "Scheduled",
    assignedTo: "Tech Team A",
  },
  {
    equipment: "Excavator CAT 320D",
    type: "Repair",
    scheduledDate: "2024-01-25",
    status: "In Progress",
    assignedTo: "Tech Team B",
  },
];

export const mockContacts = [
  {
    id: 1,
    name: "Michael Chen (Project Lead)",
    avatar: "https://i.pravatar.cc/150?u=michael",
    lastMessage: "Hi, I need a quote for a 40*60 workshop in Texas.",
    time: "2024-10-10 09:30 pm",
  },
  {
    id: 2,
    name: "Sarah Johnson (Site Manager)",
    avatar: "https://i.pravatar.cc/150?u=sarah",
    lastMessage: "Equipment delivery confirmed for tomorrow",
    time: "2024-10-10 08:15 pm",
  },
];

export const mockMessages = [
  {
    id: 1,
    sender: "Michael Chen",
    text: "Hi, I need a quote for a 40*60 workshop in Texas.",
    time: "2024-10-10 09:30 pm",
    isMe: false,
  },
  {
    id: 2,
    sender: "You",
    text: "Sure, I can help with that. Let me prepare the details.",
    time: "2024-10-10 09:35 pm",
    isMe: true,
  },
];

export const mockNotifications = [
  {
    id: 1,
    type: "alert",
    title: "Low Stock Alert",
    message: "Cement stock is below minimum level",
    time: "2 hours ago",
    read: false,
  },
  {
    id: 2,
    type: "maintenance",
    title: "Scheduled Maintenance",
    message: "Excavator CAT 320D maintenance due tomorrow",
    time: "5 hours ago",
    read: false,
  },
  {
    id: 3,
    type: "info",
    title: "Equipment Transfer",
    message: "Tower Crane transferred to Site C",
    time: "1 day ago",
    read: true,
  },
];

export const mockUserProfile = {
  fullName: "John Anderson",
  email: "johnanderson@company.com",
  phone: "+1 (555) 123-4567",
  role: "Plant Manager",
  avatar:
    "https://imgs.search.brave.com/C6AU3hqShumrOuZaswKHOeZBwOo-XeuuJnf7XZ-5QW4/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5pc3RvY2twaG90/by5jb20vaWQvMTAx/Njc0NDAzNC92ZWN0/b3IvcHJvZmlsZS1w/bGFjZWhvbGRlci1p/bWFnZS1ncmF5LXNp/bGhvdWV0dGUtbm8t/cGhvdG8uanBnP3M9/NjEyeDYxMiZ3PTAm/az0yMCZjPVJxdGky/NlZRal9mcy1faEwx/NW1KajZiODRGRVpO/YTAwRkpnWlJhRzVQ/RDQ9",
};

export const mockDashboardStats = {
  totalEquipment: 45,
  activeProjects: 12,
  maintenanceDue: 5,
  lowStockItems: 8,
};

export const mockLeadsData = {
  id: "Q-2025-1047",
  contactInfo: {
    fullName: "John Doe",
    email: "john.doe@gmail.com",
    phone: "(555) 123-4567",
    location: "Texas",
  },
  projectDetails: {
    buildingType: "Workshop",
    quoteValue: "$12,500",
    status: "Quotation Sent",
    createdOn: "2024-10-10",
  },
  assignment: {
    assignedTo: "Sarah Lee",
    role: "1 person working on this lead",
    avatar: "https://i.pravatar.cc/150?u=sarah",
  },
  contract: {
    status: "Signed contact/Agreement",
    date: "Signed on: 12 April 2025",
  },
  progress: {
    currentStep: 4,
    totalSteps: 7,
    steps: [
      { id: 1, label: "Initial Contact", status: "completed" },
      { id: 2, label: "Requirements Gathered", status: "completed" },
      { id: 3, label: "Proposal Sent", status: "completed" },
      { id: 4, label: "Negotiation", status: "current" },
      { id: 5, label: "Deal Closed", status: "pending" },
      { id: 6, label: "Payment Done", status: "pending" },
      { id: 7, label: "Delivered", status: "pending" },
    ],
  },
  recentActivity: [
    { text: "Last activity: 2024-10-18", type: "info" },
    { text: "Lead created: 2024-10-10", type: "info" },
    { text: "2 unread messages", type: "alert" },
  ],
  photos: [
    "https://imgs.search.brave.com/tHqgR22b7R8Q9j0r6QfX7y2V6V8y0r6QfX7y2V6V8y0/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5pc3RvY2twaG90/by5jb20vaWQvMTU3/NDMyNTMyL3Bob3Rv/L3N0ZWVsLWJlYW1z/LmpwZz9zPTYxMng2/MTImdz0wJms9MjAm/Yz16XzR4XzR4XzR4/XzR4XzR4XzR4XzR4",
    "https://imgs.search.brave.com/tHqgR22b7R8Q9j0r6QfX7y2V6V8y0r6QfX7y2V6V8y0/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5pc3RvY2twaG90/by5jb20vaWQvMTU3/NDMyNTMyL3Bob3Rv/L3N0ZWVsLWJlYW1z/LmpwZz9zPTYxMng2/MTImdz0wJms9MjAm/Yz16XzR4XzR4XzR4/XzR4XzR4XzR4XzR4",
    "https://imgs.search.brave.com/tHqgR22b7R8Q9j0r6QfX7y2V6V8y0r6QfX7y2V6V8y0/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5pc3RvY2twaG90/by5jb20vaWQvMTU3/NDMyNTMyL3Bob3Rv/L3N0ZWVsLWJlYW1z/LmpwZz9zPTYxMng2/MTImdz0wJms9MjAm/Yz16XzR4XzR4XzR4/XzR4XzR4XzR4XzR4",
    "https://imgs.search.brave.com/tHqgR22b7R8Q9j0r6QfX7y2V6V8y0r6QfX7y2V6V8y0/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5pc3RvY2twaG90/by5jb20vaWQvMTU3/NDMyNTMyL3Bob3Rv/L3N0ZWVsLWJlYW1z/LmpwZz9zPTYxMng2/MTImdz0wJms9MjAm/Yz16XzR4XzR4XzR4/XzR4XzR4XzR4XzR4",
  ],
};

export const mockServiceProviders = [
  {
    id: 1,
    providerName: "ABC Machinery",
    services: "Heavy Equipment",
    contact: "9876543210",
    rating: 5,
    avgCost: "High",
    lastService: "05-Apr",
  },
  {
    id: 2,
    providerName: "Local Workshop",
    services: "Repairs",
    contact: "9988776655",
    rating: 5,
    avgCost: "Low",
    lastService: "05-Apr",
  },
  {
    id: 3,
    providerName: "PowerTech",
    services: "Generators",
    contact: "9090909090",
    rating: 5,
    avgCost: "Medium",
    lastService: "05-Apr",
  },
  {
    id: 4,
    providerName: "ABC Machinery",
    services: "Heavy Equipment",
    contact: "9876543210",
    rating: 5,
    avgCost: "High",
    lastService: "05-Apr",
  },
  {
    id: 5,
    providerName: "Local Workshop",
    services: "Repairs",
    contact: "9988776655",
    rating: 5,
    avgCost: "Low",
    lastService: "05-Apr",
  },
  {
    id: 6,
    providerName: "PowerTech",
    services: "Generators",
    contact: "9090909090",
    rating: 5,
    avgCost: "Medium",
    lastService: "05-Apr",
  },
  {
    id: 7,
    providerName: "ABC Machinery",
    services: "Heavy Equipment",
    contact: "9876543210",
    rating: 5,
    avgCost: "High",
    lastService: "05-Apr",
  },
];

export const mockBreakdownCases = [
  {
    id: 1,
    equipment: "Excavator CAT 320D",
    reportedOn: "05-Apr",
    issue: "Not starting",
    severity: "Critical",
    severityColor: "bg-red-500",
    status: "Pending",
    assignedTo: "PowerTech",
  },
  {
    id: 2,
    equipment: "Concrete Mixer 350L",
    reportedOn: "05-Apr",
    issue: "Burnt motor",
    severity: "High",
    severityColor: "bg-orange-500",
    status: "Under Repair",
    assignedTo: "Local Shop",
  },
  {
    id: 3,
    equipment: "Diesel Generator 65kVA",
    reportedOn: "05-Apr",
    issue: "Trigger fault",
    severity: "Medium",
    severityColor: "bg-yellow-500",
    status: "Completed",
    assignedTo: "In-house",
  },
  {
    id: 4,
    equipment: "Excavator CAT 320D",
    reportedOn: "05-Apr",
    issue: "Not starting",
    severity: "Critical",
    severityColor: "bg-red-500",
    status: "Pending",
    assignedTo: "PowerTech",
  },
  {
    id: 5,
    equipment: "Concrete Mixer 350L",
    reportedOn: "05-Apr",
    issue: "Burnt motor",
    severity: "High",
    severityColor: "bg-orange-500",
    status: "Under Repair",
    assignedTo: "Local Shop",
  },
  {
    id: 6,
    equipment: "Diesel Generator 65kVA",
    reportedOn: "05-Apr",
    issue: "Trigger fault",
    severity: "Medium",
    severityColor: "bg-yellow-500",
    status: "Completed",
    assignedTo: "In-house",
  },
  {
    id: 7,
    equipment: "Concrete Mixer 350L",
    reportedOn: "05-Apr",
    issue: "Not starting",
    severity: "Critical",
    severityColor: "bg-red-500",
    status: "Pending",
    assignedTo: "PowerTech",
  },
];

export const taxData = [
  {
    state: "Alabama",
    filingFrequency: "Monthly",
    dueDate: "20th",
    threshold: "> $2,500/mo",
    website: "https://myalabamataxes.alabama.gov",
    status: "Paid",
  },
  {
    state: "Alaska",
    filingFrequency: "Local only",
    dueDate: "Varies",
    threshold: "Local borough/city only",
    website: "https://arsstc.org",
    status: "Pending",
  },
  {
    state: "Arizona",
    filingFrequency: "Monthly",
    dueDate: "20th",
    threshold: "> $8,000 tax/yr",
    website: "https://aztaxes.gov",
    status: "Paid",
  },
  {
    state: "Arkansas",
    filingFrequency: "Monthly",
    dueDate: "20th",
    threshold: "> $100/mo",
    website: "https://atap.arkansas.gov",
    status: "Paid",
  },
  {
    state: "California",
    filingFrequency: "Monthly",
    dueDate: "Last day",
    threshold: "> $17,000/yr",
    website: "https://onlineservices.cdtfa.ca.gov",
    status: "Pending",
  },
  {
    state: "Colorado",
    filingFrequency: "Monthly",
    dueDate: "20th",
    threshold: "> $300/mo",
    website: "https://mybiz.colorado.gov",
    status: "Paid",
  },
];

export const vendors = [
  { label: "Clean Pro", value: "Clean Pro" },
  { label: "Build Corp", value: "Build Corp" },
];

export const status = [
  { label: "Completed", value: "completed" },
  { label: "Pending", value: "pending" },
  { label: "In Progress", value: "in_progress" },
];

export const mockCostAnalysisData = [
  {
    id: 1,
    project: "HR Management System",
    category: "Labour",
    estimated: "$210,000",
    actual: "$198,000",
    variance: "-5.7%",
    varianceColor: "green",
    date: "4/8/2024",
    department: "Human Resources",
  },
  {
    id: 2,
    project: "Marketing Automation Setup",
    category: "Material",
    estimated: "$125,000",
    actual: "$135,000",
    variance: "+8.0%",
    varianceColor: "red",
    date: "4/2/2024",
    department: "Marketing",
  },
  {
    id: 3,
    project: "Quality Assurance Framework",
    category: "Labour",
    estimated: "$165,000",
    actual: "$158,000",
    variance: "-4.2%",
    varianceColor: "green",
    date: "3/25/2024",
    department: "Quality Assurance",
  },
  {
    id: 4,
    project: "CRM System Integration",
    category: "Material",
    estimated: "$195,000",
    actual: "$210,000",
    variance: "+7.7%",
    varianceColor: "red",
    date: "3/18/2024",
    department: "Sales",
  },
  {
    id: 5,
    project: "Warehouse Automation",
    category: "Logistics",
    estimated: "$450,000",
    actual: "$425,000",
    variance: "-5.6%",
    varianceColor: "green",
    date: "3/12/2024",
    department: "Operations",
  },
  {
    id: 6,
    project: "E-commerce Platform Enhancement",
    category: "Labour",
    estimated: "$320,000",
    actual: "$345,000",
    variance: "+7.8%",
    varianceColor: "red",
    date: "3/5/2024",
    department: "E-commerce",
  },
  {
    id: 7,
    project: "Security Infrastructure Upgrade",
    category: "Material",
    estimated: "$180,000",
    actual: "$175,000",
    variance: "-2.8%",
    varianceColor: "green",
    date: "3/1/2024",
    department: "IT Security",
  },
  {
    id: 8,
    project: "Data Analytics Platform",
    category: "Labour",
    estimated: "$245,000",
    actual: "$234,000",
    variance: "-4.5%",
    varianceColor: "green",
    date: "2/15/2024",
    department: "Business Intelligence",
  },
  {
    id: 9,
    project: "Customer Portal Redesign",
    category: "Material",
    estimated: "$238,000",
    actual: "$267,000",
    variance: "+12.2%",
    varianceColor: "red",
    date: "2/10/2024",
    department: "Digital Marketing",
  },
  {
    id: 10,
    project: "Supply Chain Optimization",
    category: "Logistics",
    estimated: "$274,000",
    actual: "$298,000",
    variance: "+8.8%",
    varianceColor: "red",
    date: "2/1/2024",
    department: "Operations",
  },
];

export const costAnalysisByFilter: Record<TabType, typeof mockCostAnalysisData> =
  {
    today: [
  {
    id: 1,
    project: "HR Management System",
    category: "Labour",
    estimated: "$210,000",
    actual: "$198,000",
    variance: "-5.7%",
    varianceColor: "green",
    date: "2026-01-12",
    department: "Human Resources",
  },
  {
    id: 2,
    project: "Marketing Automation Platform",
    category: "Material",
    estimated: "$125,000",
    actual: "$135,000",
    variance: "+8.0%",
    varianceColor: "red",
    date: "2026-01-12",
    department: "Marketing",
  },
  {
    id: 3,
    project: "Quality Assurance Framework",
    category: "Labour",
    estimated: "$165,000",
    actual: "$158,000",
    variance: "-4.2%",
    varianceColor: "green",
    date: "2026-01-12",
    department: "Quality Assurance",
  },
  {
    id: 4,
    project: "Customer Support Portal",
    category: "Labour",
    estimated: "$210,000",
    actual: "$198,000",
    variance: "-5.7%",
    varianceColor: "green",
    date: "2026-01-12",
    department: "Customer Service",
  },
  {
    id: 5,
    project: "Sales Reporting Dashboard",
    category: "Material",
    estimated: "$125,000",
    actual: "$135,000",
    variance: "+8.0%",
    varianceColor: "red",
    date: "2026-01-12",
    department: "Sales",
  },
  {
    id: 6,
    project: "Cloud Infrastructure Upgrade",
    category: "Labour",
    estimated: "$165,000",
    actual: "$158,000",
    variance: "-4.2%",
    varianceColor: "green",
    date: "2026-01-12",
    department: "IT Operations",
  },
  {
    id: 7,
    project: "Mobile App Redesign",
    category: "Labour",
    estimated: "$210,000",
    actual: "$198,000",
    variance: "-5.7%",
    varianceColor: "green",
    date: "2026-01-12",
    department: "Product Design",
  },
  {
    id: 8,
    project: "Data Warehouse Migration",
    category: "Material",
    estimated: "$125,000",
    actual: "$135,000",
    variance: "+8.0%",
    varianceColor: "red",
    date: "2026-01-12",
    department: "Data Engineering",
  },
  {
    id: 9,
    project: "Cybersecurity Compliance Audit",
    category: "Labour",
    estimated: "$165,000",
    actual: "$158,000",
    variance: "-4.2%",
    varianceColor: "green",
    date: "2026-01-12",
    department: "IT Security",
  },
],

    week: [
      {
        id: 4,
        project: "CRM System Integration",
        category: "Material",
        estimated: "$195,000",
        actual: "$210,000",
        variance: "+7.7%",
        varianceColor: "red",
        date: "2026-01-10",
        department: "Sales",
      },
      {
        id: 5,
        project: "Warehouse Automation",
        category: "Logistics",
        estimated: "$450,000",
        actual: "$425,000",
        variance: "-5.6%",
        varianceColor: "green",
        date: "2026-01-09",
        department: "Operations",
      },
      {
        id: 6,
        project: "E-commerce Platform Enhancement",
        category: "Labour",
        estimated: "$320,000",
        actual: "$345,000",
        variance: "+7.8%",
        varianceColor: "red",
        date: "2026-01-08",
        department: "E-commerce",
      },
      {
        id: 7,
        project: "Security Infrastructure Upgrade",
        category: "Material",
        estimated: "$180,000",
        actual: "$175,000",
        variance: "-2.8%",
        varianceColor: "green",
        date: "2026-01-07",
        department: "IT Security",
      },
    ],

    month: [
      {
        id: 8,
        project: "Data Analytics Platform",
        category: "Labour",
        estimated: "$245,000",
        actual: "$234,000",
        variance: "-4.5%",
        varianceColor: "green",
        date: "2026-01-05",
        department: "Business Intelligence",
      },
      {
        id: 9,
        project: "Customer Portal Redesign",
        category: "Material",
        estimated: "$238,000",
        actual: "$267,000",
        variance: "+12.2%",
        varianceColor: "red",
        date: "2026-01-03",
        department: "Digital Marketing",
      },
      {
        id: 10,
        project: "Supply Chain Optimization",
        category: "Logistics",
        estimated: "$274,000",
        actual: "$298,000",
        variance: "+8.8%",
        varianceColor: "red",
        date: "2026-01-01",
        department: "Operations",
      },
    ],
  } as const;



export const AnalysisCardsByFilter:AnalysisCardsByFilterType = {
  today: [
    {
      label: "Total Order Value",
      value: "$12,30,000",
      bgColor: "bg-[#E5ECFF]",
      textColor: "text-[#1D51A4]",
    },
    {
      label: "Total Plant Costs",
      value: "$9,97,000",
      bgColor: "bg-[#FEE2E2]",
      textColor: "text-[#EF4444]",
    },
    {
      label: "Projected Profit",
      value: "$2,33,000",
      bgColor: "bg-[#F0FDF4]",
      textColor: "text-[#16A34A]",
    },
  ],

  week: [
    {
      label: "Total Order Value",
      value: "$58,90,000",
      bgColor: "bg-[#E5ECFF]",
      textColor: "text-[#1D51A4]",
    },
    {
      label: "Total Plant Costs",
      value: "$46,25,000",
      bgColor: "bg-[#FEE2E2]",
      textColor: "text-[#EF4444]",
    },
    {
      label: "Projected Profit",
      value: "$12,65,000",
      bgColor: "bg-[#F0FDF4]",
      textColor: "text-[#16A34A]",
    },
  ],

  month: [
    {
      label: "Total Order Value",
      value: "$2,42,80,000",
      bgColor: "bg-[#E5ECFF]",
      textColor: "text-[#1D51A4]",
    },
    {
      label: "Total Plant Costs",
      value: "$1,95,40,000",
      bgColor: "bg-[#FEE2E2]",
      textColor: "text-[#EF4444]",
    },
    {
      label: "Projected Profit",
      value: "$47,40,000",
      bgColor: "bg-[#F0FDF4]",
      textColor: "text-[#16A34A]",
    },
  ],
} as const;


export const projectsByFilter: ProjectsByFilter = {
  today: [
    {
      name: "Residential Complex A",
      status: "In Progress",
      margin: "28.8%",
      revenue: "$1,25,000",
      costs: "$88,000",
      profit: "$36,000",
    },
    {
      name: "Commercial Building B",
      status: "Planning",
      margin: "22.4%",
      revenue: "$98,000",
      costs: "$76,000",
      profit: "$22,000",
    },
    {
      name: "Infrastructure Project C",
      status: "Execution",
      margin: "31.2%",
      revenue: "$1,45,000",
      costs: "$1,00,000",
      profit: "$45,000",
    },
  ],

  week: [
    {
      name: "Residential Complex A",
      status: "In Progress",
      margin: "30.1%",
      revenue: "$6,25,000",
      costs: "$4,20,000",
      profit: "$2,05,000",
    },
    {
      name: "Commercial Building B",
      status: "Execution",
      margin: "27.6%",
      revenue: "$5,10,000",
      costs: "$3,70,000",
      profit: "$1,40,000",
    },
    {
      name: "Industrial Warehouse D",
      status: "Planning",
      margin: "24.9%",
      revenue: "$3,80,000",
      costs: "$2,85,000",
      profit: "$95,000",
    },
  ],

  month: [
    {
      name: "Residential Complex A",
      status: "Execution",
      margin: "34.5%",
      revenue: "$22,40,000",
      costs: "$14,80,000",
      profit: "$7,60,000",
    },
    {
      name: "Commercial Building B",
      status: "Completed",
      margin: "29.2%",
      revenue: "$18,10,000",
      costs: "$12,80,000",
      profit: "$5,30,000",
    },
    {
      name: "Infrastructure Project C",
      status: "Execution",
      margin: "31.8%",
      revenue: "$25,60,000",
      costs: "$17,60,000",
      profit: "$8,00,000",
    },
  ],
};


export const paymentStatsByFilter = {
  today: [
    {
      title: "Deposit Payments",
      percentage: "85.9%",
      totalAmount: "$1,56,000",
      receivedAmount: "$1,34,000",
      pendingAmount: "$22,000",
    },
    {
      title: "Progress Payments",
      percentage: "82.2%",
      totalAmount: "$1,56,000",
      receivedAmount: "$1,34,000",
      pendingAmount: "$22,000",
    },
    {
      title: "Final Payments",
      percentage: "88.2%",
      totalAmount: "$1,56,000",
      receivedAmount: "$1,34,000",
      pendingAmount: "$22,000",
    },
  ],

  week: [
    {
      title: "Deposit Payments",
      percentage: "78.4%",
      totalAmount: "$7,85,000",
      receivedAmount: "$6,15,000",
      pendingAmount: "$1,70,000",
    },
    {
      title: "Progress Payments",
      percentage: "81.1%",
      totalAmount: "$6,40,000",
      receivedAmount: "$5,18,000",
      pendingAmount: "$1,22,000",
    },
    {
      title: "Final Payments",
      percentage: "74.9%",
      totalAmount: "$5,20,000",
      receivedAmount: "$3,90,000",
      pendingAmount: "$1,30,000",
    },
  ],

  month: [
    {
      title: "Deposit Payments",
      percentage: "88.6%",
      totalAmount: "$24,80,000",
      receivedAmount: "$21,98,000",
      pendingAmount: "$2,82,000",
    },
    {
      title: "Progress Payments",
      percentage: "84.2%",
      totalAmount: "$22,40,000",
      receivedAmount: "$18,86,000",
      pendingAmount: "$3,54,000",
    },
    {
      title: "Final Payments",
      percentage: "79.5%",
      totalAmount: "$18,60,000",
      receivedAmount: "$14,79,000",
      pendingAmount: "$3,81,000",
    },
  ],
} as const;


export const invoiceDataByFilter: InvoiceByFilter = {
  today: [
    {
      client: "John Doe",
      projectId: "Q-2025-1047",
      projectType: "Workshop . Texas",
      invoiceNo: "INV-001",
      amount: "$12,500",
      dueAmount: "Due $12,500",
      paymentDate: "25-01-2025",
      salesPerson: "John Doe",
      status: "Confirmed",
    },
    {
      client: "Alex Brown",
      projectId: "Q-2025-1091",
      projectType: "Warehouse . Florida",
      invoiceNo: "INV-002",
      amount: "$8,400",
      dueAmount: "Due $8,400",
      paymentDate: "------",
      salesPerson: "Alex",
      status: "Pending",
    },
  ],

  week: [
    {
      client: "John Doe",
      projectId: "Q-2025-1047",
      projectType: "Workshop . Texas",
      invoiceNo: "INV-010",
      amount: "$42,500",
      dueAmount: "Due $12,500",
      paymentDate: "23-01-2025",
      salesPerson: "John Doe",
      status: "Confirmed",
    },
    {
      client: "Emma Stone",
      projectId: "Q-2025-1112",
      projectType: "Retail Store . NY",
      invoiceNo: "INV-011",
      amount: "$15,300",
      dueAmount: "Due $15,300",
      paymentDate: "------",
      salesPerson: "Emma",
      status: "Pending",
    },
    {
      client: "David Lee",
      projectId: "Q-2025-1144",
      projectType: "Factory . Ohio",
      invoiceNo: "INV-012",
      amount: "$26,800",
      dueAmount: "Due $6,800",
      paymentDate: "21-01-2025",
      salesPerson: "David",
      status: "Confirmed",
    },
  ],

  month: [
    {
      client: "John Doe",
      projectId: "Q-2025-1201",
      projectType: "IT Park . Texas",
      invoiceNo: "INV-101",
      amount: "$1,20,000",
      dueAmount: "Due $20,000",
      paymentDate: "10-01-2025",
      salesPerson: "John Doe",
      status: "Confirmed",
    },
    {
      client: "Sophia Ray",
      projectId: "Q-2025-1215",
      projectType: "Hospital . LA",
      invoiceNo: "INV-102",
      amount: "$98,500",
      dueAmount: "Due $98,500",
      paymentDate: "------",
      salesPerson: "Sophia",
      status: "Pending",
    },
    {
      client: "Michael Scott",
      projectId: "Q-2025-1240",
      projectType: "Office . Seattle",
      invoiceNo: "INV-103",
      amount: "$76,200",
      dueAmount: "Due $12,200",
      paymentDate: "18-01-2025",
      salesPerson: "Michael",
      status: "Confirmed",
    },
  ],
};

export const clientBreakdownByFilter: any = {
  today: [
    {
      company: "ABC Industries",
      totalInvoiced: "$3,45,000",
      received: "$2,90,000",
      outstanding: "$55,000",
    },
    {
      company: "Delta Corp",
      totalInvoiced: "$1,85,000",
      received: "$1,40,000",
      outstanding: "$45,000",
    },
    {
      company: "Prime Solutions",
      totalInvoiced: "$2,10,000",
      received: "$1,98,000",
      outstanding: "$12,000",
    },
    {
      company: "Vertex Build",
      totalInvoiced: "$95,000",
      received: "$70,000",
      outstanding: "$25,000",
    },
    {
      company: "Skyline Infra",
      totalInvoiced: "$1,20,000",
      received: "$92,000",
      outstanding: "$28,000",
    },
  ],

  week: [
    {
      company: "ABC Industries",
      totalInvoiced: "$12,80,000",
      received: "$10,45,000",
      outstanding: "$2,35,000",
    },
    {
      company: "Delta Corp",
      totalInvoiced: "$8,45,000",
      received: "$7,10,000",
      outstanding: "$1,35,000",
    },
    {
      company: "Prime Solutions",
      totalInvoiced: "$6,90,000",
      received: "$6,25,000",
      outstanding: "$65,000",
    },
    {
      company: "Vertex Build",
      totalInvoiced: "$4,20,000",
      received: "$3,70,000",
      outstanding: "$50,000",
    },
    {
      company: "Skyline Infra",
      totalInvoiced: "$5,60,000",
      received: "$4,85,000",
      outstanding: "$75,000",
    },
  ],

  month: [
    {
      company: "ABC Industries",
      totalInvoiced: "$42,60,000",
      received: "$36,40,000",
      outstanding: "$6,20,000",
    },
    {
      company: "Delta Corp",
      totalInvoiced: "$28,40,000",
      received: "$23,75,000",
      outstanding: "$4,65,000",
    },
    {
      company: "Prime Solutions",
      totalInvoiced: "$24,90,000",
      received: "$22,10,000",
      outstanding: "$2,80,000",
    },
    {
      company: "Vertex Build",
      totalInvoiced: "$18,20,000",
      received: "$15,90,000",
      outstanding: "$2,30,000",
    },
    {
      company: "Skyline Infra",
      totalInvoiced: "$21,75,000",
      received: "$18,40,000",
      outstanding: "$3,35,000",
    },
  ],
};

export const orderSummaryByFilter: Record<
  TabType,
  OrderSummaryRecord[]
> = {
  today: [
    {
      client: "John Doe",
      projectId: "Q-2025-1047",
      location: "Workshop . Texas",
      orderValue: "$4,50,000",
      currentCost: "$3,61,000",
      deposit: "$1,35,000",
      progress: "$1,35,000",
      final: "$0",
      outstanding: "$1,35,000",
      wipProfit: "$89,000",
      margin: "19.8%",
      status: "In progress",
    },
    {
      client: "Sarah Lee",
      projectId: "Q-2025-1059",
      location: "Warehouse . Nevada",
      orderValue: "$3,10,000",
      currentCost: "$2,40,000",
      deposit: "$90,000",
      progress: "$1,10,000",
      final: "$0",
      outstanding: "$1,10,000",
      wipProfit: "$70,000",
      margin: "22.5%",
      status: "Started",
    },
    {
      client: "Michael Brown",
      projectId: "Q-2025-1064",
      location: "Retail Hub . Florida",
      orderValue: "$5,80,000",
      currentCost: "$4,92,000",
      deposit: "$2,10,000",
      progress: "$1,60,000",
      final: "$0",
      outstanding: "$2,10,000",
      wipProfit: "$88,000",
      margin: "15.2%",
      status: "In progress",
    },
  ],

  week: [
    {
      client: "Michael Brown",
      projectId: "Q-2025-1047",
      location: "Workshop . Texas",
      orderValue: "$12,80,000",
      currentCost: "$9,90,000",
      deposit: "$3,60,000",
      progress: "$3,40,000",
      final: "$0",
      outstanding: "$3,40,000",
      wipProfit: "$2,90,000",
      margin: "22.6%",
      status: "In progress",
    },
    {
      client: "Sarah Lee",
      projectId: "Q-2025-1059",
      location: "Warehouse . Nevada",
      orderValue: "$8,40,000",
      currentCost: "$6,10,000",
      deposit: "$2,40,000",
      progress: "$2,10,000",
      final: "$0",
      outstanding: "$1,90,000",
      wipProfit: "$2,30,000",
      margin: "27.4%",
      status: "Completed",
    },
    {
      client: "Michael Jordan",
      projectId: "Q-2025-1064",
      location: "Retail Hub . Florida",
      orderValue: "$10,20,000",
      currentCost: "$8,30,000",
      deposit: "$3,10,000",
      progress: "$2,90,000",
      final: "$0",
      outstanding: "$1,20,000",
      wipProfit: "$1,90,000",
      margin: "18.6%",
      status: "Started",
    },
    {
      client: "Emily Davis",
      projectId: "Q-2025-1081",
      location: "IT Park . California",
      orderValue: "$9,60,000",
      currentCost: "$7,20,000",
      deposit: "$3,00,000",
      progress: "$2,40,000",
      final: "$0",
      outstanding: "$2,20,000",
      wipProfit: "$2,40,000",
      margin: "25.0%",
      status: "In progress",
    },
  ],

  month: [
    {
      client: "John Doe",
      projectId: "Q-2025-1047",
      location: "Workshop . Texas",
      orderValue: "$42,80,000",
      currentCost: "$33,10,000",
      deposit: "$12,40,000",
      progress: "$11,60,000",
      final: "$4,20,000",
      outstanding: "$14,60,000",
      wipProfit: "$9,70,000",
      margin: "22.6%",
      status: "Completed",
    },
    {
      client: "Sarah Lee",
      projectId: "Q-2025-1059",
      location: "Warehouse . Nevada",
      orderValue: "$36,40,000",
      currentCost: "$28,20,000",
      deposit: "$10,60,000",
      progress: "$9,80,000",
      final: "$3,20,000",
      outstanding: "$12,80,000",
      wipProfit: "$8,20,000",
      margin: "22.5%",
      status: "Completed",
    },
    {
      client: "Michael Brown",
      projectId: "Q-2025-1064",
      location: "Retail Hub . Florida",
      orderValue: "$28,90,000",
      currentCost: "$23,40,000",
      deposit: "$8,90,000",
      progress: "$7,60,000",
      final: "$2,40,000",
      outstanding: "$9,10,000",
      wipProfit: "$5,50,000",
      margin: "19.0%",
      status: "In progress",
    },
    {
      client: "Emily Davis",
      projectId: "Q-2025-1081",
      location: "IT Park . California",
      orderValue: "$31,60,000",
      currentCost: "$25,10,000",
      deposit: "$9,40,000",
      progress: "$8,60,000",
      final: "$3,00,000",
      outstanding: "$10,60,000",
      wipProfit: "$6,50,000",
      margin: "20.6%",
      status: "Started",
    },
    {
      client: "David Wilson",
      projectId: "Q-2025-1099",
      location: "Manufacturing Plant . Ohio",
      orderValue: "$38,20,000",
      currentCost: "$30,40,000",
      deposit: "$11,80,000",
      progress: "$10,20,000",
      final: "$3,60,000",
      outstanding: "$12,60,000",
      wipProfit: "$7,80,000",
      margin: "20.4%",
      status: "In progress",
    },
      {
      client: "Michael Brown",
      projectId: "Q-2025-1064",
      location: "Retail Hub . Florida",
      orderValue: "$5,80,000",
      currentCost: "$4,92,000",
      deposit: "$2,10,000",
      progress: "$1,60,000",
      final: "$0",
      outstanding: "$2,10,000",
      wipProfit: "$88,000",
      margin: "15.2%",
      status: "In progress",
    },
  ],
};




export const costBreakdownByFilter: Record<
  TabType,
  {
    label: string;
    value: string;
    percentage: string;
    color: string;
    progress: number;
  }[]
> = {
  today: [
    {
      label: "Labor Cost",
      value: "$120,000",
      percentage: "58.4%",
      color: "#3B82F6",
      progress: 58.4,
    },
    {
      label: "Material Cost",
      value: "$70,000",
      percentage: "34.1%",
      color: "#22C55E",
      progress: 34.1,
    },
    {
      label: "Transportation Cost",
      value: "$15,000",
      percentage: "7.5%",
      color: "#F97316",
      progress: 7.5,
    },
  ],

  week: [
    {
      label: "Labor Cost",
      value: "$420,000",
      percentage: "59.1%",
      color: "#3B82F6",
      progress: 59.1,
    },
    {
      label: "Material Cost",
      value: "$245,000",
      percentage: "34.5%",
      color: "#22C55E",
      progress: 34.5,
    },
    {
      label: "Transportation Cost",
      value: "$46,000",
      percentage: "6.4%",
      color: "#F97316",
      progress: 6.4,
    },
  ],

  month: [
    {
      label: "Labor Cost",
      value: "$1,010,000",
      percentage: "60.2%",
      color: "#3B82F6",
      progress: 60.2,
    },
    {
      label: "Material Cost",
      value: "$565,000",
      percentage: "33.7%",
      color: "#22C55E",
      progress: 33.7,
    },
    {
      label: "Transportation Cost",
      value: "$103,000",
      percentage: "6.1%",
      color: "#F97316",
      progress: 6.1,
    },
  ],
} as const;